
import React from 'react';
import { Instagram, Facebook, Calendar, Hotel } from 'lucide-react';
import { Menu } from '@/types/menu';
import { LanguageCode, MENU_UI_TRANSLATIONS } from '@/constants/menuUITranslations';
import { HOTEL_TYPES } from '@/hooks/useEstablishmentType';

interface MenuInfoProps {
  menu: Menu;
  restaurantName?: string;
  restaurantType?: string;
  restaurantAddress?: string;
  restaurantPhone?: string;
  restaurantDescription?: string;
  currentLanguage?: LanguageCode;
  restaurantId?: string;
}

const MenuInfo: React.FC<MenuInfoProps> = ({
  menu,
  restaurantName,
  restaurantType,
  restaurantAddress,
  restaurantPhone,
  restaurantDescription,
  currentLanguage = 'fr',
  restaurantId,
}) => {
  const t = MENU_UI_TRANSLATIONS[currentLanguage];
  const isHotel = HOTEL_TYPES.includes(restaurantType || '');

  const getTranslatedDescription = () => {
    const translations = (menu.custom_css as any)?.restaurantDescriptionTranslations;
    if (translations && currentLanguage !== 'fr' && translations[currentLanguage]) {
      return translations[currentLanguage];
    }
    return restaurantDescription || menu.description;
  };

  const displayDescription = getTranslatedDescription();

  const scrollToEvents = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('events-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="px-4 pt-4 lg:px-0">
      <div className="flex flex-col">
        <h1 className="text-2xl lg:text-3xl font-bold">{restaurantName || menu.name}</h1>
        {displayDescription && (
          <p className="text-sm lg:text-base text-muted-foreground mt-1">{displayDescription}</p>
        )}
        {restaurantAddress && (
          <a 
            href={`https://maps.google.com/maps?q=${encodeURIComponent(restaurantAddress)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs lg:text-sm text-blue-600 hover:text-blue-800 mt-1 block transition-colors"
          >
            📍 {restaurantAddress}
          </a>
        )}
        {restaurantPhone && (
          <a 
            href={`tel:${restaurantPhone}`}
            className="text-xs lg:text-sm text-blue-600 hover:text-blue-800 mt-1 block transition-colors"
          >
            📞 {restaurantPhone}
          </a>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {isHotel && restaurantId && (
            <a
              href={`/hotel/${restaurantId}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              <Hotel className="h-3.5 w-3.5" />
              Tous les services
            </a>
          )}
          <a
            href="#events-section"
            onClick={scrollToEvents}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            <Calendar className="h-3.5 w-3.5" />
            {t.upcomingEvents || 'Événements'}
          </a>
          <div className="flex gap-2">
            {menu.custom_css?.socialLinks?.instagram && (
              <a 
                href={menu.custom_css.socialLinks.instagram} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Instagram size={18} />
              </a>
            )}
            {menu.custom_css?.socialLinks?.facebook && (
              <a 
                href={menu.custom_css.socialLinks.facebook} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Facebook size={18} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuInfo;
